"use client";

import type { Translate } from "@/lib/i18n/translate";
/**
 * AppToaster — the single notification surface for the whole app.
 *
 * Every `toast.*` call plus the status floaters (send/undo, compose result,
 * forwarding result — routed here via {@link ./status-floater-toast}) renders as
 * ONE top-right queue: stacked newest-on-top, each individually closable, with an
 * "Alle schließen" control once several pile up. This replaces the old
 * `position="top-center"` stack whose messages overlapped and hid one another's
 * close button.
 *
 * When the desktop workspace browser panel is mounted, the queue is portaled into
 * {@link ../main-workspace-layout/browser-panel-chrome-frame} so send/undo bars
 * and other toasts stay inside the left panel instead of covering the agent chat.
 *
 * Sonner's own palette is remapped onto the app's design tokens through inline
 * CSS variables. Inline wins over Sonner's `[data-sonner-theme]` rules
 * regardless of stylesheet order, so toasts match every surface and flip
 * correctly with the app's `.dark` class rather than the OS theme. Type is
 * carried by a calm coloured icon; only errors get a soft destructive keyline so
 * failures stand out in the queue.
 *
 * Mounted once per shell (dashboard + portal).
 */

import { useMobileViewport as useIsMobile } from "@/lib/use-mobile-viewport";
import { cn } from "@v1/ui/utils";
import { CircleCheck, Info, Loader2, TriangleAlert } from "lucide-react";
import { useTheme } from "next-themes";
import {
  type CSSProperties,
  type ReactElement,
  useEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Toaster, toast, useSonner } from "sonner";
import { useBrowserPanelToastMountOptional } from "./browser-panel-toast-mount-context";
// Side effect: wraps sonner's toast methods to scrub internal terms app-wide.
import "./sanitize-toast";
import { useTranslations } from "next-intl";

// Remap Sonner's colour/size variables onto app tokens. Declared inline (via the
// `style` prop) so they beat Sonner's own light/dark theme rules no matter the
// stylesheet order.
// Remap Sonner's colour/size variables onto app tokens. Declared inline (via the
// `style` prop) so they beat Sonner's own light/dark theme rules no matter the
// stylesheet order.
function toasterVars(
  t: Translate,
): CSSProperties & Record<`--${string}`, string> {
  return {
    "--width": "360px",
    "--border-radius": "12px",
    "--normal-bg": t("hslVarPopover1"),
    "--normal-border": t("hslVarBorder"),
    "--normal-text": t("hslVarPopoverForeground"),
    // Close-button greys → app tokens (Sonner reads these directly).
    "--gray2": t("hslVarMuted"),
    "--gray4": t("hslVarBorder"),
    "--gray5": t("hslVarBorder"),
    "--gray12": t("hslVarPopoverForeground"),
    // Solid cards — never glass. Alpha on --*-bg made error text unreadable.
    "--success-bg": t("hslVarPopover1"),
    "--success-border": t("hslVarBorder"),
    "--success-text": t("hslVarPopoverForeground"),
    "--info-bg": t("hslVarPopover1"),
    "--info-border": t("hslVarBorder"),
    "--info-text": t("hslVarPopoverForeground"),
    "--warning-bg": t("hslVarPopover1"),
    "--warning-border": t("hslVarBorder"),
    "--warning-text": t("hslVarPopoverForeground"),
    "--error-bg": t("hslVarPopover1"),
    "--error-border": t("hslVarDestructive0"),
    "--error-text": t("hslVarPopoverForeground"),
  };
}

const TOAST_ICONS = {
  success: (
    <CircleCheck
      className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
      aria-hidden
    />
  ),
  error: (
    <TriangleAlert
      className="h-4 w-4 text-red-600 dark:text-red-400"
      aria-hidden
    />
  ),
  warning: (
    <TriangleAlert
      className="h-4 w-4 text-amber-600 dark:text-amber-400"
      aria-hidden
    />
  ),
  info: (
    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden />
  ),
  loading: (
    <Loader2
      className="h-4 w-4 animate-spin text-muted-foreground"
      aria-hidden
    />
  ),
};

/**
 * Floating "Alle schließen" pill — appears only once the queue holds more than
 * one message. Anchored to the bottom-right corner, well clear of the top-right
 * toast stack (whose cards carry their close button at the top), so the pill
 * never overlaps a toast's controls. Desktop only; on mobile a full-width toast
 * with its own close button is enough.
 */
function ToastQueueControls({
  scopedToBrowserPanel,
}: {
  readonly scopedToBrowserPanel: boolean;
}): ReactElement | null {
  const t = useTranslations("shared.appToaster");
  const { toasts } = useSonner();
  if (toasts.length < 2) return null;
  return (
    <div
      className={cn(
        "z-[1000000000] hidden md:block",
        scopedToBrowserPanel
          ? "absolute bottom-3 right-4"
          : "fixed bottom-3 right-6",
      )}
    >
      <button
        type="button"
        onClick={(): void => {
          toast.dismiss();
        }}
        className={cn(
          "rounded-full border border-border/70 bg-popover/95 px-3 py-1 shadow-sm backdrop-blur",
          "text-[12px] font-medium text-primary/70 transition-colors",
          "hover:bg-accent hover:text-primary",
        )}
      >
        {t("alleSchliessen")}
      </button>
    </div>
  );
}

function AppToasterSurface({
  scopedToBrowserPanel,
  mobileBottomOffset,
}: {
  readonly scopedToBrowserPanel: boolean;
  readonly mobileBottomOffset: string;
}): ReactElement {
  const t = useTranslations("shared.appToaster");
  const { resolvedTheme } = useTheme();
  // Desktop follows the requested top-right corner; mobile stays bottom (its
  // header lives at the top and full-width toasts would cover it).
  const isMobile = useIsMobile();
  return (
    <>
      <Toaster
        position={isMobile ? "bottom-right" : "top-right"}
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        richColors
        closeButton
        expand
        gap={12}
        visibleToasts={6}
        // Both props carry the bottom offset: Sonner switches to `mobileOffset`
        // at 600px, the app's mobile breakpoint is 768px.
        offset={
          isMobile
            ? { bottom: mobileBottomOffset, right: "16px" }
            : scopedToBrowserPanel
              ? { top: "72px", right: "16px" }
              : { top: "72px", right: "24px" }
        }
        mobileOffset={{
          bottom: mobileBottomOffset,
          left: "12px",
          right: "12px",
        }}
        icons={TOAST_ICONS}
        toastOptions={{ duration: 5000 }}
        style={toasterVars(t)}
        className={
          scopedToBrowserPanel ? "!absolute pointer-events-auto" : undefined
        }
      />
      <ToastQueueControls scopedToBrowserPanel={scopedToBrowserPanel} />
    </>
  );
}

interface TrackedRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/** Viewport rect of the panel host, live-updated on panel/window resizes. */
function useTrackedElementRect(
  element: HTMLElement | null,
): TrackedRect | null {
  const [rect, setRect] = useState<TrackedRect | null>(null);

  useEffect(() => {
    if (!element) {
      setRect(null);
      return;
    }
    const update = (): void => {
      const r = element.getBoundingClientRect();
      setRect((prev) =>
        prev != null &&
        prev.top === r.top &&
        prev.left === r.left &&
        prev.width === r.width &&
        prev.height === r.height
          ? prev
          : { top: r.top, left: r.left, width: r.width, height: r.height },
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [element]);

  return rect;
}

const DEFAULT_MOBILE_BOTTOM_OFFSET = "16px";

export function AppToaster({
  mobileBottomOffset = DEFAULT_MOBILE_BOTTOM_OFFSET,
}: {
  /**
   * CSS length for the mobile bottom offset. Shells with an in-flow bottom
   * tab bar pass a `calc()` over their nav-height variable so toasts sit above
   * the bar (and above the keyboard via `--app-viewport-bottom-gap`).
   */
  readonly mobileBottomOffset?: string;
} = {}): ReactElement {
  const toastMount = useBrowserPanelToastMountOptional();
  const browserPanelHost = toastMount?.host ?? null;
  const hostRect = useTrackedElementRect(browserPanelHost);
  const scopedToBrowserPanel = browserPanelHost !== null;

  if (scopedToBrowserPanel) {
    // Body-level portal, NOT a portal into the panel frame: modals (Radix
    // dialogs) are body-portaled at z-50, and any mount inside the panel's
    // stacking context loses to them no matter its own z-index — error
    // hints ended up BEHIND open dialogs. The fixed wrapper mirrors the
    // panel's viewport rect, so toasts still appear inside the left panel
    // but always stack above dialogs.
    return createPortal(
      <div
        className="pointer-events-none fixed z-[200] overflow-visible"
        style={
          hostRect != null
            ? {
                top: hostRect.top,
                left: hostRect.left,
                width: hostRect.width,
                height: hostRect.height,
              }
            : { inset: 0 }
        }
      >
        <AppToasterSurface
          scopedToBrowserPanel
          mobileBottomOffset={mobileBottomOffset}
        />
      </div>,
      document.body,
    );
  }

  return (
    <AppToasterSurface
      scopedToBrowserPanel={false}
      mobileBottomOffset={mobileBottomOffset}
    />
  );
}
